import { useId } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { Button } from '@/libraries/design/components/button/src/button';
import { Link } from '@/libraries/design/components/link/src/link';
import { ColumnLayout, wacToken } from '@/libraries/design/foundations';
import { RichTextComponent } from '@/libraries/design/utils';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { DataEventProps } from '@/libraries/wpl/foundations/src/data-event-props';

export interface CtaProps {
	label: string;
	href: string;
	dataEventProps?: DataEventProps;
}

export type SecondaryCtaType = 'link' | 'button';

export interface BottomCallToActionProps {
	title: React.ReactElement<typeof RichTextComponent>;
	emphasisColor?: string;
	body?: React.ReactElement<typeof RichTextComponent>;
	button?: CtaProps;
	secondaryCta?: CtaProps;
	secondaryCtaType?: SecondaryCtaType;
	/**
	 * Optional URL for a background image rendered behind the CTA content.
	 * When omitted, the component uses the solid background color only.
	 */
	backgroundImage?: string;
}

const containerStyles = css({
	backgroundColor: wacToken('color.primary.black'),
	paddingBlock: token('space.800'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['p, h2, h2 > span, h2 > div']: {
		color: wacToken('color.neutral.light0'),
		textAlign: 'center',
		margin: 0,
	},
});

const backgroundImageStyles = css({
	// When a background image is present, use the lighter background color
	// (color.neutral.light1100 / #1E1F21). The default (no-image) case keeps
	// the darker color.primary.black (#101214).
	backgroundColor: wacToken('color.neutral.light1100'),
	// Guarantee at least 25px of clear space between the image and every edge,
	// at all screen sizes. We confine the background to a content box that is
	// inset 25px horizontally (the container already supplies >=25px block
	// padding via `space.800`, so its padding box gives the top/bottom inset).
	paddingInline: '25px',
	backgroundOrigin: 'content-box',
	backgroundClip: 'padding-box',
	// Mobile / smaller screens: fit the image inside the 25px-inset content box
	// preserving aspect ratio, so it never touches any edge regardless of width.
	backgroundSize: 'contain',
	backgroundPosition: 'center',
	backgroundRepeat: 'no-repeat',
	// Large and xl screens: additionally cap the image width at 432px (never
	// wider than the inset content box). Height stays `auto` to keep aspect
	// ratio. Left untouched on smaller screens to preserve mobile behavior.
	[media.above.lg]: {
		backgroundSize: 'min(100%, 432px) auto',
	},
});

const darkThemeContainerStyles = css({
	backgroundColor: 'transparent',
});

const innerContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: token('space.150'),
	[media.above.sm]: {
		paddingBlock: token('space.800'),
	},
});

const buttonContainerStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.300'),
	paddingBlockStart: token('space.400'),
});

// Rich-text `p` sits inside TypographyStyles, where refresh adds `p, .body-md` at lg+ with a
// direct font-size that overrides inheritance from `.h5`. Target paragraphs here so
// copy matches h5 (same tokens as typography-styles `.h5` ).
const bodyCopyStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& p']: {
		fontWeight: 500,
		fontSize: '1.125rem',
		lineHeight: 1.24,
		letterSpacing: 'normal',
		[media.only.sm]: {
			fontSize: '1.25rem',
		},
		[media.above.md]: {
			fontSize: '1.375rem',
			lineHeight: 1.24,
		},
		[media.above.lg]: {
			fontSize: '1.5rem',
		},
		color: wacToken('color.neutral.light0'),
		textAlign: 'center',
		margin: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& p + p']: {
		marginBlockStart: token('space.200'),
	},
});

export const BottomCallToAction = ({
	title,
	emphasisColor,
	body,
	button,
	secondaryCta,
	secondaryCtaType = 'link',
	backgroundImage,
	// eslint-disable-next-line complexity
}: BottomCallToActionProps) => {
	const uniqueId = useId();
	const titleId = `bottom-cta-title-${uniqueId}`;
	const buttonId = `bottom-cta-button-${uniqueId}`;
	const secondaryCtaId = `bottom-cta-secondary-${uniqueId}`;

	const hasSecondaryCta = secondaryCta?.href && secondaryCta?.label;

	const { isDarkTheme, isDevTheme } = useTheme();

	const emphasisStyles = css({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['em']: {
			color: emphasisColor,
			fontStyle: 'unset',
		},
	});

	return (
		<div
			css={[
				containerStyles,
				backgroundImage && backgroundImageStyles,
				isDarkTheme && darkThemeContainerStyles,
			]}
			style={backgroundImage ? { backgroundImage: `url("${backgroundImage}")` } : undefined}
		>
			<ColumnLayout layout="8">
				<div css={innerContainerStyles}>
					<h2
						id={titleId}
						className={isDevTheme ? 'mono-font-family-heading' : 'h1-lg'}
						css={[emphasisColor && emphasisStyles]}
					>
						{title}
					</h2>
					{body ? (
						<div className="h5" css={bodyCopyStyles}>
							{body}
						</div>
					) : null}
					{(button || hasSecondaryCta) && (
						<div css={buttonContainerStyles}>
							{button && (
								<Button
									id={buttonId}
									href={button.href}
									responsiveSize={{
										mobile: 'small',
										tablet: 'small',
										desktop: 'large',
										xl_desktop: 'large',
									}}
									ariaLabelledby={`${buttonId} ${titleId}`}
									dataEventProps={button.dataEventProps}
								>
									{button.label}
								</Button>
							)}
							{hasSecondaryCta &&
								(secondaryCtaType === 'button' ? (
									<Button
										id={secondaryCtaId}
										href={secondaryCta.href}
										variant="secondary"
										colorAgainst="black"
										responsiveSize={{
											mobile: 'small',
											tablet: 'small',
											desktop: 'large',
											xl_desktop: 'large',
										}}
										ariaLabelledby={`${secondaryCtaId} ${titleId}`}
										dataEventProps={secondaryCta.dataEventProps}
									>
										{secondaryCta.label}
									</Button>
								) : (
									<Link
										id={secondaryCtaId}
										text={secondaryCta.label}
										href={secondaryCta.href}
										type="primary"
										bgColor={isDarkTheme ? undefined : 'bgBlackBlue'}
										aria-label={secondaryCta.label}
										ariaLabelledby={`${secondaryCtaId} ${titleId}`}
										aria-labelledby={`${secondaryCtaId} ${titleId}`}
										dataEventProps={secondaryCta.dataEventProps}
									/>
								))}
						</div>
					)}
				</div>
			</ColumnLayout>
		</div>
	);
};
